import React from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { CSSTransition } from 'react-transition-group';
import cx from 'classnames';
import { translate } from 'react-i18next';

import CONF from 'config/conf';
import { clearEntireCustomerState, clearAllCustomers } from 'actions/customer';
import { clearSearchAndSearchFilters } from 'actions/search';
import { getTabs } from 'reducers/tabs';
import ConfirmModal from 'components/ConfirmModal';
import styles from 'styles/modules/mainTabs.module.scss';
import {
	cropTabTypeToIconName, safeQuery, customerFormNameBuilder,
	searchCustomersInAppeals, destroyFormsAppealsAndCustomers,
	closeAppeal,
} from 'helpers';
import { closeMessageModal, openMessageModal, showMenu } from 'actions/ui';
import {
	closeAllExceptCurrent,
	openAppealsFromServer,
	removeTab,
	resetTab,
	setNewCurrentTab,
	tabsLoadState,
} from 'actions/tabs';
import { clearAppealState, clearAllAppeals } from 'actions/appeal';
import { addCardsToHistory, addCardToHistory } from 'actions/user';
import { removeState, saveState } from 'utils/storage';
import TabCloseMenu from './TabCloseMenu';
import TicketIcon from 'components/Common/SVG/TicketIcon';
import HiddenTabs from './HiddenTabs';
import NewTab from './NewTab';
import { destroy } from 'redux-form';
import get from 'lodash/get';
import Tooltip from 'components/Tooltip';
import { loadAndRestoreTabs } from './utils';

function mapStateToProps(state) {
	return {
		tabs: getTabs(state),
		allTabs: state.tabs.tabs,
		currentTab: state.tabs.current,
		isAddingActive: state.tabs.isAddingActive,
		showMessageModal: state.ui.showMessageModal,
		ui: state.ui,
		isAppealUnlocked: state.appeal.unlockedAppeal,
		appeals: state.appeal.appeals,
		isRequestLoading: state.app.requests.length > 0,
		forms: state.form,
	};
}

const buildSearchKey = (entity, hash) => `search:${entity}:${hash}`;

const mapDispatchToProps = ({
	addCardToHistory,
	addCardsToHistory,
	removeTab,
	resetTab,
	closeAllExceptCurrent,
	showMenu,
	openMessageModal,
	closeMessageModal,
	clearAppealState,
	clearEntireCustomerState,
	clearAllCustomers,
	clearAllAppeals,
	clearSearchAndSearchFilters,
	destroyForm: (name) => destroy(name),
	tabsLoadState,
	openAppealsFromServer,
	setNewCurrentTab,
});

@translate()
@connect(mapStateToProps, mapDispatchToProps)
class MainTabs extends React.Component {
	state = {
		width: null,
		showMessageModal: false,
		modalTitle: '',
	};

	componentDidMount() {
		this.updateDimensions();
		window.addEventListener('resize', this.updateDimensions);

		loadAndRestoreTabs(this.props.tabsLoadState);
		this.props.openAppealsFromServer();
	}

	componentDidUpdate(prevProps) {
		const tabsChanged = prevProps.allTabs !== this.props.allTabs;
		const currentChanged = prevProps.currentTab !== this.props.currentTab;

		if (!tabsChanged && !currentChanged) {
			return;
		}

		const { allTabs, currentTab } = this.props;

		const normalizedTabs = this.normalizeTabsForStorage(allTabs);
		const normalizedCurrent = this.normalizeCurrentTab(currentTab, normalizedTabs);

		saveState({
			tabs: normalizedTabs,
			current: normalizedCurrent,
		}, 'tabsState');
	}

	componentWillUnmount() {
		window.removeEventListener('resize', this.updateDimensions);
	}

	// loadTabStateIfExist = () => {
	// 	const tabType = this.props.currentTab.type;
	// 	const loadedState = loadState(tabType);
	//
	// 	if (loadedState) {
	// 		this.props.tabsLoadState(loadedState);
	// 	}
	// };

	updateDimensions = () => this.setState({ width: window.innerWidth });

	getMainTabMaxLength = () => {
		const { width } = this.state;

		if (width > 1420) {
			return 5;
		}

		if (width > 1300) {
			return 4;
		}

		if (width > 1200) {
			return 3;
		}

		if (width > 1110) {
			return 2;
		}

		return 1;
	};

	removeTab = (e, tab) => {
		e.preventDefault();
		e.stopPropagation();

		const { history, tabs } = this.props;
		this.tab = tab;

		const path = tab.url.split('/');
		const appeal = tab.name.split('_');
		const lastTab = tabs.length === 1;

		const { name, url, type } = tab;

		if (path[1] && path[1] === 'appeals') {
			const appealId = appeal[1];
			const appealData = get(this.props.appeals, `[${appealId}]`);

			if (appealData && appealData.hasUnsavedChanges === true) {
				return this.openMessageModal(tab.displayedName || tab.regnum);
			}
		}

		if (path[1] && path[1] === 'customer') {
			this.closeCustomer(path[2], lastTab);
		}

		if (path[1] && path[1] === 'person') {
			this.closePerson(path[2], lastTab);
		}

		if (path[1] && path[1] !== 'search') {
			this.props.addCardToHistory({
				url,
				name,
			});
		}

		if (path[1] && path[1] !== 'advanced-search') {
			removeState(`advSearch:${url}`);
		}

		if (path[1] && path[1] === 'search') {
			// path: /search/:entity/:hash/...
			const entity = path[2];
			const hash = path[3];
			this.closeSearch(entity, hash);
		}

		if (type !== 'home') {
			this.props.removeTab(
				{ tabType: type },
				{
					history,
					tabs: this.props.allTabs,
					current: this.props.currentTab,
				},
			);

			if (appeal[0] === 'appeals') {
				closeAppeal({
					...this.props,
					id: appeal[1],
					lastTab,
				});
			}
		}
	};

	 closeSearch = (entity, id) => {
		const {
			destroyForm, clearSearchAndSearchFilters,
		} = this.props;

		const formSet = [`search-filters-${id}`];

		clearSearchAndSearchFilters(id);

		// remove persisted search for this tab
		 removeState(buildSearchKey(entity, id));

		setTimeout(() => {
			formSet.forEach((name) => destroyForm(name));
		}, 1000);
	};

	closeCustomer = (id, lastTab) => {
		const {
        	destroyForm, clearEntireCustomerState, appeals, forms, clearAllAppeals,
			clearAllCustomers,
		} = this.props;

		let formSet = [`${id}-edit-customer-name`];

		if (lastTab) {
			destroyFormsAppealsAndCustomers({
				forms,
				clearAllAppeals,
				destroyForm,
				clearAllCustomers,
			});
		} else {
			if (searchCustomersInAppeals(appeals, id).length === 0) {
				formSet = [
					...formSet,
					...customerFormNameBuilder(id, 'customer'),
				];

				clearEntireCustomerState(id);
			}

			setTimeout(() => {
				formSet.forEach((name) => destroyForm(name));
			}, 1000);
		}
	};

	closePerson = (id, lastTab) => {
		const { destroyForm, clearEntireCustomerState, forms, clearAllAppeals, clearAllCustomers } = this.props;

		if (lastTab) {
			destroyFormsAppealsAndCustomers({
				forms,
				clearAllAppeals,
				destroyForm,
				clearAllCustomers,
			});
		} else {
			const formSet = [`${id}-edit-person-name`, ...customerFormNameBuilder(id, 'person')];
			clearEntireCustomerState(id);

			setTimeout(() => {
				formSet.forEach((name) => destroyForm(name));
			}, 1000);
		}
	};

	removeTabStates = (tabs) => new Promise((resolve, reject) => {
		try {
			tabs.forEach((tab) => removeState(tab.type));
			resolve();
		} catch (error) {
			reject(error);
		}
	});

	removeAllTabs = (e) => {
		e.preventDefault();

		const tabs = this.props.tabs.filter((el) => el.type !== 'home');

		this.props.addCardsToHistory(tabs);
		this.props.resetTab();

		this.removeTabStates(tabs);

		this.props.history.push(this.props.tabs[0].url);
	};

	removeAllTabsExceptCurrent = (e, tab) => {
		e.preventDefault();

		const tabList = this.props.tabs;
		const tabs = tabList.filter((el) => el.type !== 'home' && el.url !== tab.url);

		this.props.addCardsToHistory(tabs);
		this.props.closeAllExceptCurrent({ url: tab.url });

		this.removeTabStates(tabs);

		this.props.history.push(tabList[tabList.length - 1].url);
	};

	showMenu = (id) => (e) => {
		if (!id) {
			return;
		}

		e.preventDefault();
		const { showMenu } = this.props;
		showMenu && showMenu({ id });
	};

	closeModal = () => {
		const { history, tabs } = this.props;
		const lastTab = tabs.length === 1;

		this.closeMessageModal();
		removeState(this.tab.type);
		const appeal = this.tab.name.split('_');

		if (appeal[0] === 'appeals') {
			closeAppeal({
				...this.props,
				id: appeal[1],
				lastTab,
			});
		}

		this.props.removeTab({
			tabType: this.tab.type,
		}, {
			history,
			tabs: this.props.allTabs,
			current: this.props.currentTab,
		});
	};

	translateName = (name) => {
		const names = ['search', 'advanced-search', 'appeals', 'customer', 'person'];
		const firstPart = name.split('_')[0];

		if (names.indexOf(firstPart) !== -1) {
			const secondPart = name
				.split('_')
				.slice(1)
				.join('_');

			return secondPart ? `${secondPart}` : '';
		}

		return name;
	};

	normalizeTab = (tab) => {
		if (!tab) {
			return tab;
		}

		if (tab.displayedName) {
			return tab;
		}

		const fallback = tab.regnum
			|| this.translateName(tab.name || '')
			|| tab.name
			|| '';

		return {
			...tab,
			displayedName: fallback,
		};
	};

	normalizeTabsForStorage = (tabs = []) => tabs.map((tab) => this.normalizeTab(tab));

	normalizeCurrentTab = (current, normalizedTabs) => {
		if (!current) {
			return current;
		}

		if (Array.isArray(normalizedTabs) && normalizedTabs.length) {
			const found = normalizedTabs.find((tab) => tab.type === current.type);

			if (found) {
				return found;
			}
		}

		return this.normalizeTab(current);
	};

	isEmptyQuery = (url) => !/^\/\w+\/\w+\/\d+\/.+$/.test(url);

	getTabText = (tab) => {
		if (tab.type.startsWith('advanced')) {
			return tab?.displayedName || this.props.t('advanced-search');
		}

		if (tab.type.startsWith('search')) {
			if (this.isEmptyQuery(tab.url)) {
				return this.props.t('Search');
			}

			return safeQuery(
				tab.url
					.split('/')
					.slice(4)
					.join('/'),
				true,
			);
		}

		if (tab.type.startsWith('appeals')) {
			return tab.regnum || tab.displayedName || this.translateName(tab.name);
		}

		if (tab.type.startsWith('customer')) {
			return `${this.translateName(tab.name)}`;
		}

		if (tab.displayedName) {
			return tab.displayedName;
		}

		return tab.name !== 'SEARCH' ? this.translateName(tab.name) : '';
	};

	getTabTitle = (tab) => {
		if (tab.type.startsWith('appeals')) {
			return tab.regnum || '';
		}

		return tab.name.length > 10 && tab.name !== 'SEARCH' ? this.translateName(tab.name) : '';
	};

	openMessageModal = (modalTitle) => this.setState({
		showMessageModal: true,
		modalTitle,
	});

	closeMessageModal = () => this.setState({
		showMessageModal: false,
		modalTitle: '',
	});

	goToTab = (nextTab) => {
		const { history } = this.props;

		history.push(nextTab.url);
		this.props.setNewCurrentTab(nextTab);
	};

	renderTab = (val, key) => {
		const { ui, currentTab, location } = this.props;

		const icon = cropTabTypeToIconName(val.type);
		const tabText = this.getTabText(val);
		const isActive = val?.url === location?.pathname && val.type === currentTab.type;

		return (
			<div
				key={key}
				className={cx('main-tab', { active: isActive })}
				title={this.getTabTitle(val)}
				onClick={() => this.goToTab(val)}
			>
				{CONF.icons[icon] === 'ticket' ? (
					<TicketIcon />
				) : CONF.icons[icon] ? (
					<i className={`icon-${CONF.icons[icon]}`} />
				) : null}

				<Tooltip
					className="main-tab-text"
					tooltip={tabText}
					position="bottom"
				>
					{tabText}
				</Tooltip>

				<TabCloseMenu
					removeTab={this.removeTab}
					removeAll={this.removeAllTabs}
					removeAllTabsExceptCurrent={this.removeAllTabsExceptCurrent}
					val={val}
					data={ui}
				/>
			</div>
		);
	};

	render() {
		const { ui, user, tabs, isAddingActive, t } = this.props;
		const { modalTitle } = this.state;
		let dropDownTabs = [];
		let mainTabs = [];
		const tabsLength = this.getMainTabMaxLength();
		const checkTabsOverflow = tabs.length > tabsLength;
		const tabsCount = tabs.length;

		if (checkTabsOverflow) {
			dropDownTabs = [...tabs?.slice(0, tabs.length - tabsLength)];
			mainTabs = [...tabs?.slice(tabs.length - tabsLength)];
		} else {
			mainTabs = [...tabs];
		}

		return (
			<nav className={styles.headTabs}>
				{checkTabsOverflow && (
					<HiddenTabs
						hiddenTabs={dropDownTabs}
						removeTab={this.removeTab}
						removeAll={this.removeAllTabs}
						removeAllTabsExceptCurrent={this.removeAllTabsExceptCurrent}
						ui={ui}
						tabs={tabs}
						translateName={this.translateName}
					/>
				)}

				{mainTabs.map(this.renderTab)}

				<NewTab ui={ui}
					user={user}
					isAddingActive={isAddingActive}
					tabsEmpty={!tabsCount}
				/>

				<CSSTransition
					appear
					enter
					exit
					mountOnEnter
					unmountOnExit
					in={this.state.showMessageModal}
					classNames="fade"
					timeout={500}
				>
					<ConfirmModal
						onClose={this.closeMessageModal}
						primaryButton
						secondaryButton
						contentModalText={t('appeal.closeModalText')}
						titleModal={modalTitle}
						primaryButtonText={t('appeal.yes')}
						secondaryButtonText={t('appeal.no')}
						onClickPrimaryButton={this.closeModal}
						onClickSecondaryButton={this.closeMessageModal}
					/>
				</CSSTransition>
			</nav>
		);
	}
}

export default withRouter(MainTabs);

MainTabs.propTypes = {

	// from withRouter HOC
	history: PropTypes.object,
	location: PropTypes.object,
	match: PropTypes.object,
	staticContext: PropTypes.bool,

	// ownProps
	ui: PropTypes.object.isRequired,
	user: PropTypes.object,

	// from connect
	tabs: PropTypes.array,
	allTabs: PropTypes.array,
	isAddingActive: PropTypes.bool,
	addCardToHistory: PropTypes.func,
	addCardsToHistory: PropTypes.func,
	removeTab: PropTypes.func,
	resetTab: PropTypes.func,
	closeAllExceptCurrent: PropTypes.func,
	showMenu: PropTypes.func, // for TabCloseMenu component
	currentTab: PropTypes.object,
	isRequestLoading: PropTypes.bool,
};
